import { useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FleetSidebar } from '@/components/layout/FleetSidebar';
import { TelemetryPanel } from '@/components/layout/TelemetryPanel';
import { WaypointPanel } from '@/components/waypoint/WaypointPanel';
import { MapView } from '@/map/MapView';
import { useMapMode } from '@/store/mapMode';
import { useSelection } from '@/store/selection';
import { rootRoute } from './__root';

function PilotWorkspace() {
  const mapMode = useMapMode((s) => s.mode);
  const focus = useSelection((s) => s.focus);
  const hasSelection = useSelection((s) => s.selected.size > 0);
  // Right pane shows the waypoint editor ONLY when we're in Waypoint mode AND
  // the operator's last interaction was waypoint-y. Clicking an asset (focus
  // = 'asset') swaps back to telemetry even mid-edit. Telemetry itself only
  // exists while a drone is selected — no selection, no right pane.
  const showWaypoints = mapMode === 'waypoint' && focus === 'waypoint';
  const showTelemetry = !showWaypoints && hasSelection;
  // Drawer state — only meaningful below `lg`. At `lg`+ both panels are static
  // and the FAB toggles are hidden via `lg:hidden`, so this state is inert.
  const [fleetOpen, setFleetOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  return (
    <div className="flex h-full">
      <FleetSidebar open={fleetOpen} onClose={() => setFleetOpen(false)} />
      <section className="relative flex flex-1 flex-col overflow-hidden">
        <div className="relative flex-1">
          <MapView />
          {/* Drawer toggles — vertical edge tabs anchored mid-height so they
              clear the top mode bar, the top-right zoom column, the bottom-left
              scale, and the bottom-right attribution. Visible only below lg. */}
          <button
            type="button"
            onClick={() => setFleetOpen(true)}
            title="Open fleet panel"
            aria-label="Open fleet panel"
            className="absolute left-0 top-1/2 z-20 grid h-12 w-6 -translate-y-1/2 place-items-center rounded-r-md border border-l-0 border-border-default bg-surface-1/95 text-fg-secondary shadow-[var(--shadow-pop)] backdrop-blur hover:text-fg-primary lg:hidden"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          {(showWaypoints || showTelemetry) && (
            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              title={showWaypoints ? 'Open waypoint panel' : 'Open telemetry panel'}
              aria-label="Open info panel"
              className="absolute right-0 top-1/2 z-20 grid h-12 w-6 -translate-y-1/2 place-items-center rounded-l-md border border-r-0 border-border-default bg-surface-1/95 text-fg-secondary shadow-[var(--shadow-pop)] backdrop-blur hover:text-fg-primary lg:hidden"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
        </div>
      </section>
      {showWaypoints && <WaypointPanel open={infoOpen} onClose={() => setInfoOpen(false)} />}
      {showTelemetry && <TelemetryPanel open={infoOpen} onClose={() => setInfoOpen(false)} />}
    </div>
  );
}

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: PilotWorkspace,
});
